import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Wallet, Sparkles } from 'lucide-react';
import { KPI } from '@/components/ui/kpi';
import { fmtBRL } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { MonthSelector } from '@/mobile/shared/MonthSelector';
import { MobileTopbar } from '@/mobile/shared/MobileTopbar';
import { HomePendencias } from './HomePendencias';
import { HomeHeroCard } from './HomeHeroCard';
import { useHomeData } from '@/mobile/hooks/useHomeData';
import type { PendItem } from './types';

const CURRENT_YEAR = 2026;
const CURRENT_MONTH = 4; // maio (0-indexed)

/** Skeleton de loading — pulse sem spinner */
function HomeSkeleton() {
  return (
    <div style={{ padding: '8px 18px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        className="animate-pulse"
        style={{ height: 120, borderRadius: 'var(--r-4)', background: 'var(--mint-softer)' }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="animate-pulse"
            style={{ height: 88, borderRadius: 'var(--r-3)', background: 'var(--card-soft)' }}
          />
        ))}
      </div>
      <div
        className="animate-pulse"
        style={{ height: 140, borderRadius: 'var(--r-3)', background: 'var(--card-soft)' }}
      />
    </div>
  );
}

/**
 * Orquestrador da tela Início mobile.
 * Gerencia seleção de mês, chama useHomeData (queries reutilizadas do desktop),
 * e compõe: Topbar · MonthSelector · HeroCard · KPI 2×2 · Pendências.
 */
export function HomeModule() {
  const [month, setMonth] = useState(CURRENT_MONTH);
  const { user } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;

  const { data, isLoading, error } = useHomeData({
    workspaceId: user?.effectiveWorkspaceId,
    monthIndex: month,
    year: CURRENT_YEAR,
  });

  function handlePendClick(item: PendItem) {
    navigate({ to: item.link.to, search: item.link.search ?? {} });
  }

  if (isLoading) return <HomeSkeleton />;

  if (error) {
    return (
      <div
        style={{
          padding: '24px 18px',
          textAlign: 'center',
          color: 'var(--block)',
          fontSize: 13,
        }}
      >
        Não conseguimos carregar os dados. Tente novamente.
      </div>
    );
  }

  const prestStatus = data?.prestStatus ?? 'rascunho';
  const lancamentosCount = data?.lancamentosCount ?? 0;
  const valorTotal = data?.valorTotal ?? 0;
  const receitas = data?.receitas ?? 0;
  const despesas = data?.despesas ?? 0;
  const saldo = data?.saldo ?? 0;
  const pendencias = data?.pendencias ?? [];

  return (
    <div
      style={{
        padding: '8px 18px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'auto',
      }}
    >
      {/* Topbar: logo + sino + avatar */}
      <MobileTopbar />

      {/* Seletor de mês */}
      <MonthSelector value={month} onChange={setMonth} year={CURRENT_YEAR} />

      {/* Hero card adaptativo */}
      <HomeHeroCard
        prestStatus={prestStatus}
        lancamentosCount={lancamentosCount}
        valorTotal={valorTotal}
        monthIndex={month}
        year={CURRENT_YEAR}
      />

      {/* KPI grid 2×2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <KPI label="Receitas" value={fmtBRL(receitas)} icon={ArrowDown} />
        <KPI label="Despesas" value={fmtBRL(despesas)} icon={ArrowUp} />
        <KPI label="Saldo" value={fmtBRL(saldo)} tone="mint" icon={Wallet} />
        <KPI label="Tempo poupado" value="92%" tone="ink" icon={Sparkles} />
      </div>

      {/* Card de pendências — só aparece quando há dados reais */}
      <HomePendencias items={pendencias} onItemClick={handlePendClick} />
    </div>
  );
}
